import { inject, provide } from 'vue';

import type { ActionPayload } from '@/interfaces/site-tree.types';

export interface Event<T> {
    action: string;
    payload: ActionPayload<T>;
    disableNodeType?: boolean;
    lockAcquired?: boolean;
}

// Create a simple event bus without mitt for now
type EventHandler = (...args: unknown[]) => void;

interface EventBus {
    on(event: string, handler: EventHandler): void;
    off(event: string, handler?: EventHandler): void;
    emit(event: string, ...args: unknown[]): void;
}

class SimpleEventBus implements EventBus {
    private events: Record<string, EventHandler[]> = {};

    on(event: string, handler: EventHandler): void {
        if (!this.events[event]) {
            this.events[event] = [];
        }
        this.events[event].push(handler);
    }

    off(event: string, handler?: EventHandler): void {
        if (!this.events[event]) return;

        if (handler) {
            this.events[event] = this.events[event].filter((h) => h !== handler);
        } else {
            delete this.events[event];
        }
    }

    emit(event: string, ...args: unknown[]): void {
        if (!this.events[event]) return;

        this.events[event].forEach((handler) => {
            try {
                handler(...args);
            } catch (error) {
                console.error('Event handler error:', error);
            }
        });
    }
}

export const eventBus = new SimpleEventBus();

export function provideEventBus() {
    provide('eventBus', eventBus);
}

export function useEventBus() {
    const bus = inject('eventBus');
    if (!bus) {
        throw new Error('Event bus not provided');
    }
    return bus as typeof eventBus;
}
